/**
 * Domain types for the Usage Categories page — the tenant's task-categorization
 * taxonomy (the system classification categories), owned and persisted by the
 * context service. The page edits a local draft tree and saves the diff back.
 */

/**
 * The taxonomy is a fixed three-level contract (top-level → task group →
 * specific task), matching bmai-context's projection cap and the ETL classifier's
 * drill-down. A node at the deepest level (depth 2, zero-based) may gain no
 * children — neither manually nor via AI assist — so nothing authored here is
 * silently truncated downstream.
 */
export const MAX_TAXONOMY_LEVELS = 3;

/** Whether a node at `depth` (0 = top level) may gain children — the cap that
 * keeps the tree at three levels. Gates both add-child and AI-assist. */
export function canAddBeneathDepth(depth: number): boolean {
    return depth < MAX_TAXONOMY_LEVELS - 1;
}

/** Which representation of the taxonomy the page shows. */
export type CategoriesView = 'list' | 'chart';

/** A persisted taxonomy node as read from the context service (a built tree). */
export interface UsageTaxonomyNode {
    id: string;
    label: string;
    description: string;
    enabled: boolean;
    sortOrder: number;
    children: UsageTaxonomyNode[];
}

/** The full usage-category taxonomy plus the context dimension that owns it. */
export interface UsageTaxonomy {
    dimensionId: string | null;
    nodes: UsageTaxonomyNode[];
}

/** A node's state in the working draft relative to the persisted baseline. */
export type TaxonomyChangeStatus = 'unchanged' | 'new' | 'edited';

/**
 * A node in the editable draft tree. `serverId` is null until the node is
 * persisted; `draftId` is a stable client key for React and parent references.
 */
export interface TaxonomyDraftNode {
    draftId: string;
    serverId: string | null;
    label: string;
    description: string;
    enabled: boolean;
    sortOrder: number;
    status: TaxonomyChangeStatus;
    children: TaxonomyDraftNode[];
}

/** Fields persisted when creating or updating a taxonomy node. */
export interface TaxonomyNodeInput {
    label: string;
    parentTaxonomyId: string | null;
    description: string;
    enabled: boolean;
    sortOrder: number;
}

/** Aggregate counts shown in the page header. */
export interface TaxonomyStats {
    categories: number;
    subcategories: number;
    disabled: number;
    pendingChanges: number;
}

/** A draft node flattened for the single tree-table view, with its render context. */
export interface TaxonomyRowView {
    node: TaxonomyDraftNode;
    depth: number;
    hasChildren: boolean;
    expanded: boolean;
    parentDraftId: string | null;
}
